'use client';

// useSuggestedQuestions - TanStack Query hook for suggested questions

import { useQuery } from '@tanstack/react-query';
import type { SuggestedQuestionsResponse } from './types';

/**
 * Query key factory for suggested questions
 */
const queryKeys = {
  all: ['suggested-questions'] as const,
  suggestions: (botId: string) => [...queryKeys.all, botId] as const,
};

/**
 * Fetch suggested questions from API
 */
async function fetchSuggestedQuestions(
  botId: string
): Promise<SuggestedQuestionsResponse['data']> {
  const response = await fetch(`/api/bots/${botId}/suggested-questions`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch suggested questions');
  }

  const result: SuggestedQuestionsResponse = await response.json();
  return result.data;
}

export interface UseSuggestedQuestionsOptions {
  /** Bot ID to get suggestions for */
  botId: string;
  /** Whether to enable the query */
  enabled?: boolean;
}

export interface UseSuggestedQuestionsReturn {
  /** Suggested questions (exactly 3) */
  suggestions: string[];
  /** Topics the bot can answer about */
  topics: string[];
  /** Whether suggestions are being loaded */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Refetch suggestions */
  refetch: () => void;
}

/**
 * Hook for fetching suggested questions
 *
 * @param options - Hook options
 * @returns Suggested questions and loading state
 *
 * @example
 * ```tsx
 * const { suggestions, topics, isLoading } = useSuggestedQuestions({
 *   botId: 'bot-123',
 * });
 *
 * // Display suggestions
 * suggestions.map(q => <Chip label={q} />)
 * ```
 */
export function useSuggestedQuestions({
  botId,
  enabled = true,
}: UseSuggestedQuestionsOptions): UseSuggestedQuestionsReturn {
  const queryKey = queryKeys.suggestions(botId);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchSuggestedQuestions(botId),
    enabled: enabled && !!botId,
    staleTime: 5 * 60 * 1000, // 5 minutes - regenerate on content change
    refetchOnWindowFocus: false, // Prevent rotation - AI generates different questions each time
    refetchOnMount: false, // Use cached questions when remounting
    retry: 2,
  });

  return {
    suggestions: data?.questions || [],
    topics: data?.topics || [],
    isLoading,
    error: error instanceof Error ? error : null,
    refetch: () => refetch(),
  };
}

export default useSuggestedQuestions;
